import { useEffect, useState, useRef } from "react";
import { ShieldCheck, Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { isValidEmail } from "../../lib/validate";
import { CaptchaBox, captchaRequired } from "../CaptchaBox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OtpField = ({ email, onToken }) => {
  const [phase, setPhase] = useState("idle"); // idle | sending | sent | verifying | verified
  const [code, setCode] = useState("");
  const [captcha, setCaptcha] = useState(null);
  const capRef = useRef(null);

  useEffect(() => {
    setPhase("idle");
    setCode("");
    onToken(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const send = async () => {
    if (!isValidEmail(email)) { toast.error("Enter a valid email first, e.g. abc@gmail.com"); return; }
    if (captchaRequired() && !captcha) { toast.error("Please complete the security check first"); return; }
    setPhase("sending");
    try {
      const res = await fetch(`${API}/otp/send`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, captcha_token: captcha || undefined }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Could not send OTP");
      setPhase("sent");
      toast.success("Verification code sent to your email");
    } catch (e) { toast.error(e.message); setPhase("idle"); capRef.current?.reset(); setCaptcha(null); }
  };

  const verify = async () => {
    setPhase("verifying");
    try {
      const res = await fetch(`${API}/otp/verify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Verification failed");
      onToken(d.otp_token);
      setPhase("verified");
      toast.success("Email verified");
    } catch (e) { toast.error(e.message); setPhase("sent"); }
  };

  if (phase === "verified") {
    return (
      <p data-testid="otp-verified" className="inline-flex items-center gap-2 font-body text-sm text-gf-primary border border-gf-primary/30 px-4 py-2.5">
        <ShieldCheck className="w-4 h-4" /> Email verified
      </p>
    );
  }

  if (phase === "sent" || phase === "verifying") {
    return (
      <div className="flex gap-3">
        <input
          data-testid="otp-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="6-digit code"
          inputMode="numeric"
          className="flex-1 max-w-[180px] bg-gf-bg border border-gf-primary/40 px-4 py-2.5 font-mono text-sm tracking-[0.3em] text-gf-text placeholder:tracking-normal placeholder:text-gf-muted focus:outline-none"
        />
        <button type="button" data-testid="otp-confirm-btn" onClick={verify} disabled={code.length !== 6 || phase === "verifying"}
          className="inline-flex items-center gap-2 bg-gf-primary text-gf-bg font-body font-bold text-sm px-5 py-2.5 hover:bg-gf-primary-hover transition-colors disabled:opacity-50">
          {phase === "verifying" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
        </button>
        <button type="button" data-testid="otp-resend-btn" onClick={send}
          className="font-body text-xs text-gf-muted hover:text-gf-primary underline">
          Resend
        </button>
      </div>
    );
  }

  return (
    <div>
      <CaptchaBox ref={capRef} onVerify={setCaptcha} />
      <button type="button" data-testid="otp-send-btn" onClick={send} disabled={phase === "sending"}
        className="inline-flex items-center gap-2 border border-gf-primary/40 text-gf-primary font-body font-bold text-sm px-5 py-2.5 hover:bg-gf-primary/10 transition-colors disabled:opacity-60">
        {phase === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailCheck className="w-4 h-4" />}
        Verify email (OTP)
      </button>
    </div>
  );
};
